import React, { useState } from 'react'
import {
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  Checkbox,
  FormControlLabel,
  SelectChangeEvent,
  Alert
} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PersonIcon from '@mui/icons-material/Person'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Address } from 'hooks/usePayment'
import {
  formatPhoneNumber,
  formatZipCode,
  validatePhoneNumber,
  validateZipCode
} from 'utils/formatUtils'
import AddressFields from 'components/AddressFields'
import NameFields from 'components/NameFields'
import { useTranslation } from 'react-i18next'

type AddressFormProps = {
  initialValues: Address
  onSubmit: (address: Address) => Promise<void>
  onCancel: () => void
  mode: 'add' | 'edit'
}

const AddressForm: React.FC<AddressFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  mode
}) => {
  const { t } = useTranslation()
  const [formValues, setFormValues] = useState({
    firstName: initialValues.customerName.split(' ')[0] || '',
    lastName: initialValues.customerName.split(' ')[1] || '',
    phoneNumber: formatPhoneNumber(initialValues.phoneNumber),
    companyName: initialValues.companyName,
    addressLine1: initialValues.addressLine1,
    addressLine2: initialValues.addressLine2,
    city: initialValues.city,
    state: initialValues.state,
    zipCode: initialValues.zipCode,
    isPrimaryAddress: initialValues.isPrimaryAddress
  })

  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    companyName: '',
    addressLine1: '',
    city: '',
    state: '',
    zipCode: ''
  })

  const [error, setError] = useState<boolean>(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    let formattedValue = value

    if (name === 'phoneNumber') {
      formattedValue = formatPhoneNumber(value)
    }

    if (name === 'zipCode') {
      formattedValue = formatZipCode(value)
    }

    setFormValues({
      ...formValues,
      [name]: formattedValue
    })
  }

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    let errorMessage = ''

    if (
      name === 'firstName' ||
      name === 'lastName' ||
      name === 'companyName' ||
      name === 'addressLine1' ||
      name === 'city' ||
      name === 'state'
    ) {
      errorMessage = value.trim() === '' ? 'This field is required.' : ''
    } else if (name === 'phoneNumber') {
      errorMessage = validatePhoneNumber(value)
        ? ''
        : 'Phone number format should be 123-456-7890'
    } else if (name === 'zipCode') {
      errorMessage = validateZipCode(value)
        ? ''
        : 'Zip Code must be in 5-digit or ZIP+4 format (e.g., 12345 or 12345-6789).'
    }

    setErrorMessages(prev => ({
      ...prev,
      [name]: errorMessage
    }))
  }

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target
    if (name) {
      setFormValues({ ...formValues, [name]: value })
      setErrorMessages(prev => ({
        ...prev,
        [name]: value.trim() === '' ? 'This field is required.' : ''
      }))
    }
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, isPrimaryAddress: e.target.checked })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const newErrors: Record<string, string> = {
      firstName: formValues.firstName.trim() ? '' : 'First name is required.',
      lastName: formValues.lastName.trim() ? '' : 'Last name is required.',
      phoneNumber: validatePhoneNumber(formValues.phoneNumber)
        ? ''
        : 'Phone number format should be 123-456-7890',
      companyName: formValues.companyName.trim()
        ? ''
        : 'Company Name is required.',
      addressLine1: formValues.addressLine1.trim()
        ? ''
        : 'Address Line 1 is required.',
      city: formValues.city.trim() ? '' : 'City is required.',
      state: formValues.state.trim() ? '' : 'State is required.',
      zipCode: validateZipCode(formValues.zipCode)
        ? ''
        : 'Zip Code must be in 5-digit or ZIP+4 format (e.g., 12345 or 12345-6789).'
    }

    setErrorMessages(newErrors)

    const hasErrors = Object.values(newErrors).some(error => error !== '')
    setError(hasErrors)

    if (!hasErrors) {
      try {
        const newAddress = {
          ...initialValues,
          customerName: `${formValues.firstName} ${formValues.lastName}`,
          phoneNumber: formValues.phoneNumber,
          companyName: formValues.companyName,
          addressLine1: formValues.addressLine1,
          addressLine2: formValues.addressLine2,
          city: formValues.city,
          state: formValues.state,
          zipCode: formValues.zipCode,
          isPrimaryAddress: formValues.isPrimaryAddress
        }
        await onSubmit(newAddress)
      } catch (error) {
        console.error('Error submitting address details:', error)
      }
    }
  }

  return (
    <Box sx={{ p: 4 }}>
      {error && (
        <Alert
          severity='error'
          icon={<ErrorOutlineIcon />}
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontWeight: 500
            }
          }}
        >
          {t('myAccount.addressBook.errors.allFieldsRequired')}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Typography
          variant='h5'
          sx={{
            fontWeight: 700,
            color: 'text.primary'
          }}
        >
          {mode === 'edit'
            ? t('myAccount.addressBook.editAddress')
            : t('myAccount.addressBook.addNewAddress')}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box
        component='form'
        noValidate
        autoComplete='off'
        onSubmit={handleSubmit}
      >
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              {t('myAccount.addressBook.contactInformation')}
            </Typography>
          </Box>
          <NameFields
            formValues={{
              firstName: formValues.firstName,
              lastName: formValues.lastName
            }}
            errors={{
              firstName: errorMessages.firstName,
              lastName: errorMessages.lastName
            }}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
          />

          <Box display='flex' gap={2}>
            <TextField
              label='Phone Number'
              name='phoneNumber'
              value={formValues.phoneNumber}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder='Required'
              fullWidth
              margin='dense'
              variant='outlined'
              required
              error={Boolean(errorMessages.phoneNumber)}
              helperText={errorMessages.phoneNumber}
              inputProps={{ maxLength: 12 }}
              size='small'
            />

            <TextField
              label='Company Name'
              name='companyName'
              value={formValues.companyName}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder='Required'
              fullWidth
              margin='dense'
              variant='outlined'
              required
              error={Boolean(errorMessages.companyName)}
              helperText={errorMessages.companyName}
              inputProps={{ maxLength: 50 }}
              size='small'
            />
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocationOnIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              {t('myAccount.addressBook.address')}
            </Typography>
          </Box>

          <AddressFields
            formValues={{
              addressLine1: formValues.addressLine1,
              addressLine2: formValues.addressLine2,
              city: formValues.city,
              state: formValues.state,
              zipCode: formValues.zipCode
            }}
            errors={{
              addressLine1: errorMessages.addressLine1,
              city: errorMessages.city,
              state: errorMessages.state,
              zipCode: errorMessages.zipCode
            }}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
            handleSelectChange={handleSelectChange}
          />
        </Box>

        {mode === 'edit' && !initialValues.isPrimaryAddress && (
          <Box
            sx={{
              bgcolor: 'grey.50',
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              mb: 3
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.isPrimaryAddress}
                  onChange={handleCheckboxChange}
                  name='isPrimaryAddress'
                />
              }
              label={t('myAccount.addressBook.setPrimary')}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontWeight: 600
                }
              }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            variant='outlined'
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            fullWidth
            sx={{
              py: 1,
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 400
            }}
          >
            {t('myAccount.addressBook.goBack')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            type='submit'
            startIcon={<SaveIcon />}
            fullWidth
            sx={{
              py: 1,
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 400
            }}
          >
            {t('myAccount.addressBook.saveAddress')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default AddressForm
